import { supabase } from "@/integrations/supabase/client";
import { Sale } from "@/models/sale";
import { startOfMonth, endOfMonth, format } from "date-fns";

// Función auxiliar para obtener el porcentaje de comisión de un cliente por nombre
const getClientCommissionPercentage = async (clientName: string, sellerId: string): Promise<number> => {
  try {
    const { data: client, error } = await supabase
      .from('clients')
      .select(`
        commission_type_id,
        commission_types:commission_type_id (
          percentage
        )
      `)
      .eq('name', clientName)
      .eq('seller_id', sellerId)
      .single();

    if (error || !client) {
      console.warn(`No se pudo encontrar comisión para cliente: ${clientName}`);
      return 0;
    }

    return (client as any).commission_types?.percentage || 0;
  } catch (err) {
    console.warn(`Error al obtener comisión del cliente ${clientName}:`, err);
    return 0;
  }
};

export const fetchSales = async (filterByCurrentMonth: boolean = false): Promise<Sale[]> => {
  try {
    let query = supabase
      .from('ads')
      .select(`
        id,
        client_name,
        start_date,
        amount,
        screen_id,
        seller_id,
        seller_name,
        seller_email,
        screens:screen_id (id, name)
      `);
    
    // Si se solicita filtrar por mes actual
    if (filterByCurrentMonth) {
      const currentDate = new Date();
      const startOfCurrentMonth = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const endOfCurrentMonth = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      
      console.log(`Filtrando ventas desde ${startOfCurrentMonth} hasta ${endOfCurrentMonth}`);
      
      // Filtramos anuncios que comenzaron en este mes
      query = query.gte('start_date', startOfCurrentMonth)
                  .lte('start_date', endOfCurrentMonth);
    }
    
    const { data: adsData, error: adsError } = await query;

    if (adsError) {
      console.error('Error al cargar ventas:', adsError);
      return [];
    }
    
    if (!adsData) return [];
    
    // Transformar las ventas incluyendo el porcentaje de comisión del cliente
    const transformedSales: Sale[] = await Promise.all(
      adsData.map(async (ad) => {
        const commissionPercentage = await getClientCommissionPercentage(ad.client_name, ad.seller_id);
        
        return {
          id: ad.id,
          sellerId: ad.seller_id,
          sellerName: ad.seller_name || 'Desconocido',
          sellerEmail: ad.seller_email || null,
          adId: ad.id,
          clientName: ad.client_name,
          commissionPercentage, // Nuevo campo con el porcentaje correcto
          screenId: ad.screen_id,
          screenName: getScreenName(ad.screens),
          amount: ad.amount,
          date: ad.start_date
        };
      })
    );
    
    return transformedSales;
  } catch (err) {
    console.error('Error al cargar ventas:', err);
    return [];
  }
};

// Función auxiliar para extraer el nombre de la pantalla de forma segura
function getScreenName(screens: any): string {
  // Si screens es un objeto y tiene propiedad name, usar ese valor
  if (screens && typeof screens === 'object' && !Array.isArray(screens) && 'name' in screens) {
    return screens.name || 'Desconocida';
  }
  // En cualquier otro caso, retornar valor por defecto
  return 'Desconocida';
}
